/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 *
 * EOS Step 2 data orchestration service — loads initial data for both
 * STUDY and COUNTRY level flows, refreshes workflow state, loads admin users,
 * and manages config checksums for change detection between steps.
 *
 * Migrated from: app/view/app/eos/set-up/step2/eosSetUpStep2.data.service.js
 */

import { eosApiService } from '../api/api.eos'
import { usersApiService } from '../api/users'
import { getUserId } from '../auth'
import { EOS_CONFIG_LEVEL, EOS_WORKFLOW_STATUS } from '../../constants/eos'
import * as eosStudyService from './eosSetUpStep2.study.service'
import * as eosCountryService from './eosSetUpStep2.country.service'
import { getIsMccEnabled } from './eos.service'
import type { EosConfig, EosOptions, EosAvailableUser, EosSite } from '../../types/eos'

// ---------------------------------------------------------------------------
// loadData — main entry point for Step 2 data loading
// ---------------------------------------------------------------------------

/**
 * Loads all data needed for Step 2.
 * For COUNTRY level: loads study owners + countries, then preloads all sites.
 * For STUDY level: loads admin users + study-level data (sites + workflows) in parallel.
 *
 * Mutates config and options in-place (mirrors legacy behavior).
 */
export async function loadData(config: EosConfig, options: EosOptions): Promise<void> {
  if (config.level === EOS_CONFIG_LEVEL.COUNTRY) {
    const countryData = await eosCountryService.loadCountryLevelData(config, options)

    Object.assign(options, {
      studyOwners: countryData.studyOwners,
      countriesMap: countryData.countriesMap
    })

    await eosCountryService.preloadAllSites(config, options)
  } else {
    const [usersData, studyLevelData] = await Promise.all([
      loadAvailableUsers(),
      eosStudyService.loadStudyLevelData(config, options)
    ])

    Object.assign(config, {
      workflowInitiator: usersData.workflowInitiator
    })

    Object.assign(options, {
      sitesMap: studyLevelData.sitesMap,
      availableUsers: usersData.availableUsers,
      activeWfs: studyLevelData.activeWfs
    })
  }
}

// ---------------------------------------------------------------------------
// refreshData — re-fetch workflows to detect cancellations
// ---------------------------------------------------------------------------

/**
 * Refreshes workflow state for STUDY level.
 * Detects cancelled workflows and reloads affected site users.
 * No-op for COUNTRY level.
 *
 * Mutates options in-place.
 */
export async function refreshData(config: EosConfig, options: EosOptions): Promise<void> {
  if (config.level !== EOS_CONFIG_LEVEL.STUDY) {
    return
  }

  const studyUuid = getIsMccEnabled(config) && config.environment ? config.environment!.uuid : config.study!.uuid

  const activeWfs = (await eosApiService.getFolderWfs(config.folder!.id, studyUuid)) as EosSite[]

  Object.assign(options, { activeWfs })

  // Detect cancelled workflows and reload users for those sites
  const reloadPromises: Promise<void>[] = []

  for (const site of Object.values(options.sitesMap)) {
    const prevWf = site.activeWf
    const currentWf = (activeWfs as unknown[]).find(
      (wf: unknown) =>
        (wf as { folderName?: string; status?: string }).folderName === site.sanitizedName &&
        (wf as { status?: string }).status === EOS_WORKFLOW_STATUS.ACTIVE
    ) as EosSite['activeWf']

    if (prevWf && !currentWf) {
      // Workflow has been cancelled — reload users
      reloadPromises.push(eosStudyService.reloadUsers(site, config))
    }

    Object.assign(site, { activeWf: currentWf })
  }

  // Wait for all reload requests, but don't fail if some fail
  await Promise.allSettled(reloadPromises)
}

// ---------------------------------------------------------------------------
// loadAvailableUsers — admin users for workflow initiator dropdown
// ---------------------------------------------------------------------------

interface AvailableUsersResult {
  availableUsers: EosAvailableUser[]
  workflowInitiator: EosAvailableUser | null
}

/**
 * Loads admin users from the assembled users/groups cache.
 * Sets the currently logged-in user as the default workflow initiator
 * if they are an admin.
 */
export async function loadAvailableUsers(): Promise<AvailableUsersResult> {
  const data = await usersApiService.getAssembledUsersGroups()
  const currentUserId = getUserId()

  let workflowInitiator: EosAvailableUser | null = null
  const availableUsers = (data.users as EosAvailableUser[]).filter((user) => {
    const isAdmin = user.role === 'ADMIN'
    if (isAdmin && user.id === currentUserId) {
      workflowInitiator = user
    }
    return isAdmin
  })

  return { availableUsers, workflowInitiator }
}

// ---------------------------------------------------------------------------
// Checksum — detect config changes between steps
// ---------------------------------------------------------------------------

/**
 * Computes a checksum string from the config fields that matter for change detection.
 */
export function getChecksum(config: EosConfig): string {
  const { selectedFolder, study, environment, studyGroup, level } = config
  return JSON.stringify({ selectedFolder, study, environment, studyGroup, level })
}

/**
 * Sets the checksum on the config object (mutates in-place).
 */
export function setChecksum(config: EosConfig): void {
  config.checksum = getChecksum(config)
}
